// The sums behind every financial screen. All in JOD unless a name says otherwise.
import { jordanDate } from '@serb/shared';
import { addDays, daysBetween } from '../../lib/dates';
import { toJod } from '../../lib/currency';
import type { Shipment } from '@serb/shared';
import type { Tone } from '../../components/tone';
import type { FinRecord, InvoiceStatus } from './sample';

export const STATUS_TONE: Record<InvoiceStatus, Tone> = {
  'Pending Invoice': 'neutral',
  Invoiced: 'info',
  'Partially Paid': 'warning',
  Paid: 'success',
};

const RATE_STATUSES = ['Invoiced', 'Partially Paid', 'Paid'];

/** One shipment's money, worked out */
export function financials(r: FinRecord, ships: Shipment[]) {
  const today = jordanDate();
  const ship = ships.find((s) => s.srb === r.srb);
  const parts = [r.agent, r.carrier, r.local];
  const known = parts.every((p) => p !== null);
  const j = (n: number | null) => (n === null ? 0 : toJod(n, r.currency));
  const revenue = toJod(r.price, r.currency);
  const costs = known ? parts.reduce<number>((sum, p) => sum + toJod(p ?? 0, r.currency), 0) : null;
  const profit = costs === null ? null : revenue - costs;
  const invoiced = RATE_STATUSES.includes(r.status);
  const date = addDays(today, -r.daysAgo);
  return {
    r,
    ship,
    revenue,
    costs,
    profit,
    margin: profit === null ? null : (profit / revenue) * 100,
    agentJod: j(r.agent),
    carrierJod: j(r.carrier),
    localJod: j(r.local),
    invoiced,
    date,
    /** what the customer still owes on the invoice */
    owed: invoiced ? revenue * (1 - r.paidShare) : 0,
    dueDate: addDays(today, r.dueInDays),
    overdue: invoiced && r.paidShare < 1 && r.dueInDays < 0,
    // SERB owes the agent once the agent's invoice is in (the sample data always has one)
    agentOwed: r.agent !== null && !r.agentPaid && (r.agentInvoiced ?? true) ? j(r.agent) : 0,
    agentDue: addDays(today, r.agentDueInDays),
    agentOverdue: r.agent !== null && !r.agentPaid && (r.agentInvoiced ?? true) && r.agentDueInDays < 0,
  };
}
export type Fin = ReturnType<typeof financials>;

/** Totals over some shipments: profit, the average margin, what is owed each way, and the profit per container, per kg and per CBM */
export function summarize(all: Fin[]) {
  // sold = booked or arrived (the real data), or invoiced (the sample data)
  const sold = all.filter((f) => f.r.won ?? f.invoiced);
  const costed = sold.filter((f) => f.costs !== null);
  const revenue = sold.reduce((n, f) => n + f.revenue, 0);
  const costs = costed.reduce((n, f) => n + (f.costs ?? 0), 0);
  const profit = costed.reduce((n, f) => n + (f.profit ?? 0), 0);
  // the average of each shipment's own margin
  const margins = costed.map((f) => f.margin ?? 0);
  return {
    revenue,
    costs,
    profit,
    avgMargin: margins.length ? margins.reduce((a, b) => a + b, 0) / margins.length : 0,
    receivables: all.reduce((n, f) => n + f.owed, 0),
    payables: all.reduce((n, f) => n + f.agentOwed, 0),
    shipments: all.length,
    ...unitProfits(costed),
  };
}

/**
 * Average profit per unit, for each way of shipping: per container (FCL), per kg (air), per CBM (LCL).
 * It is the total profit of those shipments divided by their total units, or `null` when there are none.
 */
export function unitProfits(costed: Fin[]) {
  const per = (mode: 'FCL' | 'Air' | 'LCL', units: (f: Fin) => number) => {
    const group = costed.filter((f) => f.ship?.mode === mode);
    const total = group.reduce((n, f) => n + units(f), 0);
    return total > 0
      ? { value: group.reduce((n, f) => n + (f.profit ?? 0), 0) / total, shipments: group.length }
      : null;
  };
  return {
    perContainer: per('FCL', (f) => f.ship?.containers ?? 0),
    perKg: per('Air', (f) => f.ship?.chargeableKg ?? 0),
    perCbm: per('LCL', (f) => f.ship?.cbm ?? 0),
  };
}

/** The month label of a date: "Oct" */
export const monthName = (date: string) =>
  new Date(date + 'T00:00:00Z').toLocaleDateString('en-GB', { month: 'short', timeZone: 'UTC' });

/** Per month of this year up to now: revenue, costs and profit of the invoiced shipments */
export function monthly(all: Fin[]) {
  const today = jordanDate();
  const upTo = Number(today.slice(5, 7));
  const months = Array.from({ length: upTo }, (_v, i) => `${today.slice(0, 4)}-${String(i + 1).padStart(2, '0')}`);
  const rows = months.map((m) => summarize(all.filter((f) => f.date.slice(0, 7) === m)));
  return {
    labels: months.map((m) => monthName(m + '-01')),
    revenue: rows.map((r) => r.revenue),
    costs: rows.map((r) => r.costs),
    profit: rows.map((r) => r.profit),
  };
}

/** Whole days from today to a date (negative = in the past) */
export const daysFromToday = (date: string) => daysBetween(jordanDate(), date);

/** "Sea - FCL" / "Sea - LCL" / "Air" for a shipment's freight mode */
export const freightMode = (f: Fin) =>
  f.ship?.mode === 'FCL' ? 'Sea - FCL' : f.ship?.mode === 'LCL' ? 'Sea - LCL' : f.ship?.mode === 'Air' ? 'Air' : 'Road';
